const addToCartGetters = {
  /**
   * getters.addToCartModel
   * Cart Interaction
   *
   * Includes the data required for the cart service REST call
   *
   * @param {number} state.quantity: quantity to add to cart
   * @param {number} getters.computedSelectedSku.skuId: currently selected sku # (10 digits)
   */
  addToCartModel(state, getters) {
    const {
      selectedStore
    } = state.shipping;
    const {
      computedSelectedShippingMethod
    } = getters;
    return {
      sku: getters.computedSelectedSku.skuId,
      storeId: "8000",
      checkoutOrderDataIncluded: true,
      quantity: state.quantity,
      selectedStoreId: computedSelectedShippingMethod === "PICKUP" ? selectedStore.store.storeId : null,
      termsCategoryAgreedTo: state.acceptTermsConditions,
      includeMembership: getters.includeMembershipInAddToCart,
      // #Todo: Add in functionality for these later
      splashCategory: "",
      wishlistEntryId: ""
    };
  },
  addToCartMicroserviceModel(state, getters) {
    const membership = {
      sku: getters.appPropsMap.textStrings.membershipSkuId,
      quantity: 1
    };
    return [{
      sku: getters.computedSelectedSku.skuId,
      quantity: state.quantity,
      isTermsAndConditionsAgreed: state.acceptTermsConditions,
      checkForInventory: true,
      onlyAddAvailableInventory: true,
      hasCustomerAgreedToBackOrder: false,
      selectedStoreId: getters.computedSelectedShippingMethod === "PICKUP" ? state.shipping.selectedStore.store.storeId : null
    }, ...getters.includeMembershipInAddToCart ? [membership] : []];
  },
  /**
   * getters.backOrderRedirect
   * return the backOrderRedirect path (sans the return URL)
   *
   * @param {string} getters.appPropsMap.endpoints.backorder:
   *   backorder endpoint
   * @param {Object} getters.addToCartModel: model for ATC POST
   * @returns
   */
  backorderRedirect(state, getters) {
    const backorderEndPoint = getters.appPropsMap.endpoints.backorder;
    const {
      wishlistEntryId,
      ...backorderParams
    } = getters.addToCartModel;
    const queryParams = Object.entries(backorderParams).map((param) => param.join("=")).join("&");
    return `${backorderEndPoint}?${queryParams}`;
  },
  bonusCardData(state, getters) {
    var _a, _b;
    const bonusCard = {
      hasBonusCard: false,
      bonusCardAmount: void 0,
      bonusCardCopy: void 0
    };
    const bonusCardItem = (_b = (_a = state.cartMicroserviceData) == null ? void 0 : _a.lineItems) == null ? void 0 : _b.find((item) => {
      var _a2;
      return ((_a2 = item == null ? void 0 : item.cartItem) == null ? void 0 : _a2.isPromoCard) === true;
    });
    if (bonusCardItem) {
      bonusCard.hasBonusCard = true;
      bonusCard.bonusCardAmount = bonusCardItem.giftCardDetails.amount;
      bonusCard.bonusCardCopy = getters.appPropsMap.textStrings.atcBonusCard;
    }
    return bonusCard;
  },
  /**
   * getters.canAddToCart
   * Cart Interaction
   *
   * @param {number} state.quantity
   * @param {boolean} getters.isSkuSelected
   * @return {boolean} Master validation property for add to cart
   */
  canAddToCart(state, getters) {
    const {
      membershipMessaging: {
        includeMembership
      },
      showMemberOnlyAddToCart
    } = getters;
    return getters.isSkuSelected && state.quantity > 0 && getters.selectedSizeIsAvailable && getters.selectedColorIsAvailable && (showMemberOnlyAddToCart ? includeMembership === true : true) && getters.hasValidFulfillment;
  },
  cartErrorData(state) {
    const {
      cartMicroserviceErrorData
    } = state;
    const tokenErr = 10002;
    const termsErr = 10213;
    const backorderErrs = [10223, 10224, 10225];
    const lineItemCountErr = 10219;
    const membershipErrs = [3508, 3509];
    const code = Array.isArray(cartMicroserviceErrorData == null ? void 0 : cartMicroserviceErrorData.cartItemIssueDtoList) ? cartMicroserviceErrorData == null ? void 0 : cartMicroserviceErrorData.cartItemIssueDtoList[0].code : void 0;
    const title = code === lineItemCountErr ? "You've reached the maximum number of items allowed in your cart." : "Oops, something went wrong!";
    const isZeroAvailability = cartMicroserviceErrorData == null ? void 0 : cartMicroserviceErrorData.isZeroAvailability;
    let scenario;
    if (code === tokenErr) scenario = "token";
    if (code === termsErr) scenario = "terms";
    if (backorderErrs.includes(code) && isZeroAvailability) scenario = "backorder-zero";
    else if (backorderErrs.includes(code)) scenario = "backorder";
    if (code === lineItemCountErr) scenario = "limit";
    if (membershipErrs.includes(code)) scenario = "membership";
    return {
      title,
      scenario,
      hasDefinedError: scenario !== void 0
    };
  },
  cartErrorNumberOfItemsAvailable(state) {
    var _a, _b, _c;
    let available;
    const pattern = /\d+$/g;
    const message = (_b = (_a = state.cartMicroserviceErrorData) == null ? void 0 : _a.cartItemIssueDtoList) == null ? void 0 : _b[0].message;
    if (typeof message === "string") available = (_c = message.match(pattern)) == null ? void 0 : _c[0];
    return available;
  },
  /**
  * getters.cartItemData
  * Add to Cart Modal
  *
  * @param {Object} getters.computedPrice.minPrice: price data for sku added to cart
  * @param {string} getters.computedImagesForColor[0].uri: URL for sku featured image
  * @param {string} state.product.brand.name: Brand name (e.g. "North Face")
  * @param {string} state.product.title: Product name without the Brand
  * @param {Object} state.cartData: response data from most recent Cart Service POST
  * @param {String} getters.justForMembersThankYouCopy: String for add to cart modal
  */
  cartItemData(state, getters) {
    var _a, _b, _c;
    const noCart = !((_a = state.cartMicroserviceData) == null ? void 0 : _a.cart);
    if (noCart) return {};
    const {
      sale,
      value,
      compValue
    } = getters.computedPrice.minPrice;
    const {
      membershipCost
    } = getters.appPropsMap.textStrings;
    const {
      quantity
    } = state;
    const price = {
      sale: sale * quantity,
      value: value * quantity,
      compValue: compValue * quantity
    };
    const order = {
      totalItemQuantity: state.cartMicroserviceData.cart.totalItemQuantity,
      totalProduct: state.cartMicroserviceData.total
    };
    const rrComponent = state.richRelevance.component;
    const showKeyValue = (k, v) => (k == null ? void 0 : k.toLowerCase()) === "none" ? false : !!(k && v);
    const color = getters.computedSelectedColor.displayLabel;
    const {
      colorLabel
    } = state.product.displayOptions;
    const {
      sizeLabel,
      computedSelectedSize: size
    } = getters;
    const showColor = showKeyValue(color, colorLabel);
    const showSize = showKeyValue(size, sizeLabel);
    const imageSize = state.showAddToCartInterstitial ? "?size=440" : "?size=140x160";
    const showXlBreakpoint = (_c = (_b = getters.appPropsMap) == null ? void 0 : _b.launchToggles) == null ? void 0 : _c.enableAtcXlBreakpoint;
    return {
      ...getters.bonusCardData,
      imageSrc: `${getters.computedMediaCenterImages[0].baseUri}${imageSize}`,
      brandName: state.product.brand.name,
      productName: state.product.title,
      quantity,
      price,
      subtotal: order.totalProduct,
      totalQuantity: order.totalItemQuantity,
      recsComponent: rrComponent,
      isPreorder: getters.isPreorder,
      includeMembership: state.includeMembership,
      isJustForMembersCopy: getters.justForMembersThankYouCopy,
      membershipCost,
      color,
      colorLabel,
      showColor,
      size,
      sizeLabel,
      showSize,
      membershipBillboardProps: getters.membershipBillboardProps,
      overRequestMessage: state.overRequestMessage,
      recsPlacements: getters.interstitialRecsPlacements,
      showXlBreakpoint
    };
  },
  /**
   * getters.justForMembersThankYouCopy
   *
   * @param {Boolean} getters.unifiedCustomerData
   * @param {String} getters.appPropsMap.textStrings
   * @returns {String} copy for 'thank you' message in member-only add to cart modal
   */
  justForMembersThankYouCopy(state, getters) {
    if (getters.computedIsMemberOnly) {
      return getters.unifiedCustomerData.isMember ? getters.appPropsMap.textStrings.atcThankYouMemberCopy : getters.appPropsMap.textStrings.atcThankYouNonmemberCopy;
    }
    return "";
  },
  /**
   * getters.computedWishlist
   * Third Party / other buttons
   *
   * Shaping data for wishlist button/interaction (REI endpoint)
   * Wishlists have:
   *   name: display name for wishlist - user defined or default "Wish List"
   *   id: id # used in requests and to create link to wishlist
   *   type: "List" (user created list) or "WishList" (primary wishlist)
   *
   * @params from state:
   * @param {string} wishlist.selectedWishlist.list.id: selected wishlist id
   * @param {string} wishlist.selectedWishlist.list.name: selected wishlist name
   * @param {Array<Object>} wishlist.allWishlists: wishlists available for user
   * @param {number} quantity: quantity added to wishlist
   * @param {string} product.styleId: style # added to wishlist
   *
   * @param {string} getters.computedSelectedSku.skuId: sku # added to wishlist
   */
  computedWishlist(state, getters) {
    return {
      analytics: {
        linkName: "add to wishlist",
        clickData: {
          wishlistID: `${state.wishlist.selectedWishlist.list.id}`,
          product: state.product.styleId,
          productSkus: getters.computedSelectedSku.skuId
        }
      },
      selectedWishlist: {
        name: state.wishlist.selectedWishlist.list.name,
        id: state.wishlist.selectedWishlist.list.id,
        link: `/lists/${state.wishlist.selectedWishlist.list.id}`,
        type: state.wishlist.selectedWishlist.list.type
      },
      wishlistItem: {
        sku: getters.computedSelectedSku.skuId,
        quantity: state.quantity
      },
      wishlistUrl: `${getters.appPropsMap.endpoints.wishlist}?toUrl=${state.product.canonicalUrl}`,
      allWishlists: state.wishlist.allWishlists.map((wl) => ({
        name: `${wl.list.name}${wl.list.type === "WishList" ? " (Primary)" : ""}`,
        id: wl.list.id,
        link: `/lists/${wl.list.id}`,
        type: wl.list.type
      }))
    };
  },
  wishlistEntries(state) {
    return (state.wishlist.allWishlists || []).reduce((acc, {
      list = {}
    }) => [...acc, ...list.entries], []).map((entry) => entry.sku);
  },
  isSkuInWishlist(state, getters) {
    return getters.wishlistEntries.includes(getters.computedSelectedSku.skuId);
  },
  /**
   * getters.computedWishListError
   * Third Party / other buttons
   *
   * @param {string} state.wishlist.errorCode: error code returned from wishlist service
   * @param {boolean} state.wishlist.hasError: true if has an error
   */
  computedWishlistError(state) {
    const {
      errorCode,
      hasError
    } = state.wishlist;
    let errorMessage = "Something didn't go as planned, please try again.";
    if (errorCode === 1604) errorMessage = "List no longer available, please try again.";
    if (errorCode === 1601) errorMessage = "Sorry, since you didn't create this list you are unable to make changes.";
    return {
      hasError,
      errorMessage
    };
  },
  /**
   * getters.getLocationsErrorMessage
   * @param {*} state.storeData.geoLocationError tracks error responses from browser geo service
   * Has shape {code: <Number>, message: <String>}
   * @param {*} getters.appPropsMap.textString app prop, uses:
   *   geoErrorGeneric: <String>, geoErrorBlocked: <String>,
   *   geoErrorPosition: <String>, geoErrorTimeout: <String>
   * @param {*} getters.appPropsMap.textStrings app prop strings for
   *   fulfillmentErrorUnknown: <String>, fulfillmentErrorNoLocations: <String>
   * @returns {String} Computed error message for geolocation API
   */
  getLocationsErrorMessage(state, getters) {
    const {
      textStrings
    } = getters.appPropsMap;
    const {
      geoLocationError,
      fulfillmentError
    } = state.storeData;
    if (fulfillmentError) return textStrings.fulfillmentErrorUnknown;
    if (!geoLocationError) return textStrings.geoErrorGeneric;
    const {
      code
    } = geoLocationError;
    if (code === 1) return textStrings.geoErrorBlocked;
    if (code === 2) return textStrings.geoErrorPosition;
    if (code === 3) return textStrings.geoErrorTimeout;
    return textStrings.geoErrorGeneric;
  },
  /**
   * getters.findInStoreModalProps
   * @param {string} state.storeData.searchTerm
   * @param {array<object>} getters.transformedBlueYonderStoreData
   * @returns {object} a map of props for the StorePickupModal.
   * Needed to make the modal content reactive. used in ModalContainer.js
   */
  findInStoreModalProps(state, getters) {
    var _a, _b;
    const storePickupRestriction = (_b = (_a = getters == null ? void 0 : getters.computedSelectedSku) == null ? void 0 : _a.shippingRestrictions) == null ? void 0 : _b.storePickupRestriction;
    let size = getters.computedSelectedSize;
    if (!size || size.toLowerCase() === "none") size = "";
    return {
      brandName: state.product.brand.name,
      btnText: "Change store",
      hasError: state.storePickupValidation.hasError,
      hasWarning: state.storePickupValidation.hasWarning,
      imageSrc: getters.computedMediaCenterImages[0].uri,
      loading: state.storeData.isLoading,
      noStorePickupMessage: storePickupRestriction || "",
      preferredStoreId: getters.computedSelectedStoreId,
      productName: state.product.title,
      problemMessage: state.storePickupValidation.problemMessage,
      searchTerm: state.storeData.searchTerm,
      size,
      storeList: getters.transformedBlueYonderStoreData,
      showGeo: state.storeData.allowsGeo,
      showInStockStores: state.limitStoresToInStock
    };
  },
  hasMembershipInCart(state) {
    var _a, _b, _c, _d, _e;
    return ((_a = state.cartSummary) == null ? void 0 : _a.hasMembership) === true || ((_c = (_b = state.cartMicroserviceData) == null ? void 0 : _b.cart) == null ? void 0 : _c.containsMembership) === true || ((_e = (_d = state.cartData) == null ? void 0 : _d.order) == null ? void 0 : _e.containsMembership) === true;
  },
  includeMembershipInAddToCart(state, getters) {
    return state.includeMembership && !getters.hasMembershipInCart;
  },
  hasValidFulfillment(state, getters) {
    return getters.hasValidShip || getters.hasValidPickup;
  },
  hasValidColor(state, getters) {
    return getters.isColorSelected && getters.selectedColorIsAvailable;
  },
  hasValidSize(state, getters) {
    return getters.isSizeSelected && getters.selectedSizeIsAvailable;
  },
  interstitialRecsPlacements(state) {
    var _a, _b;
    const {
      placements: {
        addToCartRecs,
        altAddToCartRecs,
        curationOutfitting
      }
    } = state.richRelevance;
    const hasCurationOutfitting = ((_a = curationOutfitting == null ? void 0 : curationOutfitting.items) == null ? void 0 : _a.length) > 0;
    const trimmedCurationOutfitting = {
      ...curationOutfitting,
      items: (_b = curationOutfitting == null ? void 0 : curationOutfitting.items) == null ? void 0 : _b.slice(0, 6)
    };
    return {
      primary: hasCurationOutfitting ? trimmedCurationOutfitting : addToCartRecs,
      secondary: hasCurationOutfitting ? addToCartRecs : altAddToCartRecs,
      tertiary: hasCurationOutfitting ? altAddToCartRecs : null
    };
  }
};
export {
  addToCartGetters as default
};
//# sourceMappingURL=addToCartGetters.mjs.map
